import { DesignSystem } from "./Components/DesignSystem";
import { SavedCaptionDesignSystemConfig , CaptionDesignSystem} from "./Components/Captions/Caption";
import { BackgroundDesignSystem, BackgroundDesignSystemConfig } from "./Components/Background/Background";
import { WatermarkDesignSystem, WatermarkDesignSystemConfig } from "./Components/Watermark/Watermark";
import { SpeakerDesignSystem, SpeakerDesignSystemConfig } from "./Components/Speaker/Speaker";
import { VideoMultiFocusDesignSystem, VideoMultiFocusDesignSystemConfig } from "./Components/VideoMultiFocus/VideoMultiFocus";
import { VideoDesignSystemConfig } from "./Components/Video/Video";
import {Theme} from "../Components/TemplateConfigurator/ThemeConfig/ThemeConfig";
import dataScheme from "./video_data_to_render.json";
import React from "react";
import { TrebbleClipCreatorInputSchema } from "./TrebbleClipCreatorSchema";


export type TemplateInputConfig = {
  designSystem: DesignSystem;
  savedCaptionConfig?: SavedCaptionDesignSystemConfig;
  showCaption: boolean;
  overrideThemeCaption: boolean;
  backgroundDesignConfig: BackgroundDesignSystemConfig;
  watermarkDesignConfig?: WatermarkDesignSystemConfig;
  speakerDesignConfig?: SpeakerDesignSystemConfig;
  videoConfig: VideoMultiFocusDesignSystemConfig;
  savedThemeConfig: Theme;
}

export type VideoInputConfig = {
  durationInMs: number;
  framePerSec: number;
  audioUrl: string;
  videoRenderData: typeof dataScheme;
  startTimeOffset: number;
  firstScheduledNode: any;
  inCloudRenderingEnvironment: boolean;
  videoTierResolution?: string;
} ;

export type TrebbleClipCreatorInputConfig = {
  templateInputConfig: TemplateInputConfig;
  videoInputConfig: VideoInputConfig;

} ;

export type VideoConfig = {
  type: "singleFocus",
  config: VideoDesignSystemConfig
} | {
  type: "dualFocus",
  config: VideoMultiFocusDesignSystemConfig
}

export const TrebbleClipCreatorInput: React.FC<TrebbleClipCreatorInputSchema> = ({templateInputConfig:config, videoInputConfig}) => {
  // Get the appropriate design system based on theme
  const { designSystem, savedThemeConfig } = config;
  const { brandConfig } = savedThemeConfig.themeConfig || {};
  const { canvas } = designSystem;

  const effectiveDesignSystem: DesignSystem = brandConfig 
    ? {
        ...designSystem,
        primaryColor: brandConfig.primaryColor || designSystem.primaryColor,
        secondaryColor: brandConfig.secondaryColor || designSystem.secondaryColor,
        accentColor: brandConfig.accentColor || designSystem.accentColor,
        titleFont: brandConfig.titleFont || designSystem.titleFont,
        bodyFont: brandConfig.bodyFont || designSystem.bodyFont,
        logoURL: brandConfig.logoURL || designSystem.logoURL,
        titleFontOutline: brandConfig.titleFontOutline || designSystem.titleFontOutline,
        bodyFontOutline: brandConfig.bodyFontOutline || designSystem.bodyFontOutline,
        borderRadius: brandConfig.borderRadius || designSystem.borderRadius,
        canvas
      }
    : designSystem;

  // Get the appropriate caption config based on override setting
  const effectiveCaptionConfig = config.overrideThemeCaption && config.savedThemeConfig.themeConfig?.savedCaptionConfig 
      ? config.savedCaptionConfig 
      : config.savedThemeConfig.themeConfig?.savedCaptionConfig;

  const captionDesign = effectiveCaptionConfig 
      ? new CaptionDesignSystem(
          effectiveCaptionConfig.captionConfig,
          effectiveDesignSystem, 
          videoInputConfig.videoRenderData, 
          videoInputConfig.startTimeOffset, 
          videoInputConfig.firstScheduledNode, 
          videoInputConfig.inCloudRenderingEnvironment
      ) 
      : null;

  const backgroundDesignSystem = new BackgroundDesignSystem(config.savedThemeConfig.themeConfig.backgroundConfig, effectiveDesignSystem, videoInputConfig.inCloudRenderingEnvironment);

  const videoDesignSystem = new VideoMultiFocusDesignSystem(config.videoConfig as VideoMultiFocusDesignSystemConfig, effectiveDesignSystem, videoInputConfig.videoRenderData, videoInputConfig.startTimeOffset, videoInputConfig.inCloudRenderingEnvironment, videoInputConfig.firstScheduledNode);

  const watermarkDesignSystem = config.watermarkDesignConfig ? new WatermarkDesignSystem(config.watermarkDesignConfig, effectiveDesignSystem, videoInputConfig.inCloudRenderingEnvironment) : null;

  const speakerDesignSystem = config.speakerDesignConfig ? new SpeakerDesignSystem(config.speakerDesignConfig, effectiveDesignSystem, videoInputConfig.inCloudRenderingEnvironment) : null;

  return (
    <div>
      {/* Blurred Background Image */}
      {backgroundDesignSystem.render()}

      {/* Main Video (Series of clips) */}
      {videoDesignSystem.render()}

      {/* Captions */}
      {config.showCaption && captionDesign?.render()}

      {/* Logo */}
      {config.watermarkDesignConfig?.showWatermark && watermarkDesignSystem?.render()}

      {/* Speaker Info */}
      {config.speakerDesignConfig?.showSpeakerLabels && speakerDesignSystem?.render()}
    </div>
  );
};


export default TrebbleClipCreatorInput;